import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { X, Baby, CheckCircle, XCircle, BookOpen, Plus, Pencil, UserCheck } from "lucide-react";
import toast from "react-hot-toast";
import { deleteRegisteredUser, updateRegisteredUser } from "@/services/users";
import UserModal from "./UserModal";
import AddChildModal from "@/components/modals/AddChildModal";
import GenericTable from "@/components/ui/GenericTable";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import PaymentModal from "../modals/PaymentModal";
import PaymentProcessingLoader from "../modals/PaymentProgressingModal";
import useBuyNow from "@/hooks/useBuyNow";
import Pagination from "../common/Pagination";
import { getChildHistoryService, getChildren, updateChild } from "@/services/parent";
import DatePicker from "../ui/DatePicker";
import { fetchMatchingDiscountChildren } from "@/lib/utils/discount";
import storage from "@/lib/storage";
import ConfirmationModal from "../modals/ConfirmationModal";

interface UserListProps {
  users: any[];
  loading: boolean;
  onRefresh: () => void;
  searchQuery: string;
  statusFilter: string;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}

interface PaymentDateFormData {
  payment_status_date: string;
}

export default function UserList({
  users,
  loading,
  onRefresh,
  searchQuery,
  statusFilter,
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
}: UserListProps) {
  const { canCreate, canUpdate } = useScreenPermission("resgistered profile");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any | null>(null);
  const [viewingUser, setViewingUser] = useState<any | null>(null);
  const [justCreatedUser, setJustCreatedUser] = useState<string | null>(null);
  const [viewingChildren, setViewingChildren] = useState<{ user: any; children: any[] } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [childModalOpen, setChildModalOpen] = useState(false);
  const [editingChild, setEditingChild] = useState<any>(null);
  const [childParentUsername, setChildParentUsername] = useState("");
  const { canCreate: canCreateChild, canUpdate: canUpdateChild } = useScreenPermission("child");
  const [selectedUsername, setSelectedUsername] = useState("");
  const [renewalChild, setRenewalChild] = useState<boolean>(false);
  const [manualPayLoading, setManualPayLoading] = useState<number | null>(null);
  const [showManualPaidConfirmation, setShowManualPaidConfirmation] = useState(false);
  const [manualPaymentModal, setManualPaymentModal] = useState<{
    open: boolean;
    child: any | null;
    nextStatus: "Paid";
  }>({
    open: false,
    child: null,
    nextStatus: "Paid",
  });

  const [manualRemarks, setManualRemarks] = useState("");
  const [remarksError, setRemarksError] = useState("");
  const [childHistory, setChildHistory] = useState<any[]>([]);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  // ── Local form just for the Payment Date field (DatePicker requires react-hook-form control) ──
  const {
    control: dateControl,
    watch: watchDate,
    reset: resetDateForm,
    setError: setDateError,
    clearErrors: clearDateErrors,
    formState: { errors: dateErrors },
  } = useForm<PaymentDateFormData>({
    defaultValues: { payment_status_date: "" },
  });
  const manualPaymentDate = watchDate("payment_status_date");

  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [localUsers, setLocalUsers] = useState(users);
  const userData = storage.getUserData();
  const isSuperUser = userData?.isSuperuser

  const {
    openBuyNowModal,
    paymentData,
    openBuyNow,
    closeBuyNow,
    handleBuyNow,
    isProcessingPayment,
    rewardBalance,
    offers,
    specialOffers,
    selectedOfferAmount,
    setSelectedOfferAmount,
    usedRewardAmount,
    setUsedRewardAmount,
    paymentCalculation,
    showSavingsModal,
    selectedSpecialOfferAmount,
    setSelectedSpecialOfferAmount,
  } = useBuyNow({
    username: selectedUsername,
    onSuccess: async () => {
      await onRefresh();
    },
    onRefresh: async () => {
      await onRefresh();
    }
  });

  useEffect(() => {
    if (!viewingChildren) return;

    const latestUser = users.find(
      (item: any) => item.id === viewingChildren.user.id
    );
    if (latestUser) {
      setViewingChildren({
        user: latestUser,
        children: latestUser.children || [],
      });
    }
  }, [users]);

  const anyModalOpen =
    isModalOpen ||
    !!viewingUser ||
    !!viewingChildren ||
    childModalOpen ||
    !!showDeleteConfirm ||
    manualPaymentModal.open ||
    openBuyNowModal ||
    !!justCreatedUser;

  useEffect(() => {
    document.body.style.overflow = anyModalOpen ? "hidden" : "auto";

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [anyModalOpen]);

  useEffect(() => {
    setLocalUsers(users);
  }, [users]);

  async function getChildHistory(childId: string) {
    try {
      setHistoryLoading(true);

      const response = await getChildHistoryService(childId);
      const data = response.data as any;

      setChildHistory(data?.Child_History || []);
      setHistoryModalOpen(true);
    } catch (error: any) {
      console.error("GET CHILD HISTORY ERROR:", error);

      toast.error(
        error?.response?.data?.message ||
        "Unable to get child history"
      );
    } finally {
      setHistoryLoading(false);
    }
  }

  const handleAdd = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const handleEdit = (user: any) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) return;

    setDeleting(true);
    try {
      const response = await deleteRegisteredUser(showDeleteConfirm.id);
      if (response.status === 200 || response.status === 204) {
        toast.success("User deleted successfully");
        setShowDeleteConfirm(null);
        await onRefresh();
      } else {
        toast.error("Failed to delete user");
      }
    } catch (error) {
      toast.error("Error deleting user");
    } finally {
      setDeleting(false);
    }
  };

  const handlePayChild = async (child: any) => {
    try {
      const parentUsername = viewingChildren?.user?.Username || "";

      if (!parentUsername) {
        toast.error("Parent username is missing");
        return;
      }

      const response = await getChildren(parentUsername);

      let latestChild = child;

      if (response.status === 200 && response.data) {
        const data = response.data as any;

        const userData = Array.isArray(data)
          ? data[0]
          : data;

        const childList =
          userData?.childs ||
          userData?.Childs ||
          [];

        if (Array.isArray(childList)) {
          latestChild =
            childList.find(
              (c: any) =>
                String(c.id) === String(child.id)
            ) || child;

          // setChildren(childList);
        }
      }

      const planName =
        latestChild?.Plan_Name || "";

      const range =
        latestChild?.Range?.Period ||
        latestChild?.Range ||
        "";
      const price = latestChild?.Range?.Base_Price


      if (!planName || !range) {
        toast.error("Plan or range is missing");
        return;
      }

      const existingDiscount = Number(
        latestChild?.child_discount || 0
      );

      const existingDiscountAmount = Number(
        latestChild?.child_discount_amount || 0
      );

      const savedFinalAmount = Number(
        latestChild?.Range?.Final_Amount ??
        latestChild?.Final_Amount ??
        latestChild?.Amount ??
        0
      );

      let fullAmount = savedFinalAmount;

      if (
        existingDiscount > 0 &&
        existingDiscountAmount > 0
      ) {
        fullAmount = Number(
          (
            savedFinalAmount +
            existingDiscountAmount
          ).toFixed(2)
        );
      }

      if (
        !Number.isFinite(fullAmount) ||
        fullAmount <= 0
      ) {
        toast.error("Unable to calculate subscription amount");
        return;
      }

      const matchingChildren =
        await fetchMatchingDiscountChildren({
          username: parentUsername,
          planName,
          range,
          type: "Subscribed",
          currentChild: latestChild, price
        });

      console.log("PARENT USERNAME:", parentUsername);
      console.log("MATCHING DISCOUNT CHILDREN:", matchingChildren);

      const hasMatchingChild =
        matchingChildren.length > 0;

      const childDiscount =
        hasMatchingChild ? 50 : 0;

      const childDiscountAmount =
        hasMatchingChild
          ? Number(
            (fullAmount * 0.5).toFixed(2)
          )
          : 0;

      const childPayableAmount =
        Number(
          (
            fullAmount -
            childDiscountAmount
          ).toFixed(2)
        );
      openBuyNow({
        subscriptionCode:
          latestChild?.Subscription_Code || "",
        Range: latestChild.Range,
        childIds: [
          latestChild.id,
        ],

        totalAmount:
          childPayableAmount,

        items: [
          {
            planName:
              latestChild?.Plan_Name || "",

            amount:
              childPayableAmount,

            duration:
              latestChild?.Range?.Period || "",

            range:
              latestChild?.Lal_Lon_Km ??
              latestChild?.DistanceKm ??
              0,
          },
        ],

        child: {
          ...latestChild,

          child_original_amount:
            fullAmount,

          child_discount:
            childDiscount,

          child_discount_amount:
            childDiscountAmount,

          Range: {
            ...latestChild?.Range,

            Final_Amount:
              childPayableAmount,
          },
        },
      });
    } catch (error: any) {
      console.error("PAY CHILD ERROR:", error);

      toast.error(
        error?.response?.data?.message ||
        "Unable to prepare payment"
      );
    }
  };

  const handleManualPaid = async () => {
    if (!manualPaymentModal.child) return;

    let hasError = false;

    if (!manualRemarks.trim()) {
      setRemarksError("Remarks are required");
      hasError = true;
    }

    if (!manualPaymentDate) {
      setDateError("payment_status_date", {
        type: "manual",
        message: "Payment date is required",
      });
      hasError = true;
    }

    if (hasError) return;

    setShowManualPaidConfirmation(true);
  };

  const confirmManualPaid = async () => {
    if (!manualPaymentModal.child) return;

    try {
      setShowManualPaidConfirmation(false);
      setManualPayLoading(manualPaymentModal.child.id);

      const response = await updateChild(manualPaymentModal.child.id, {
        payment_status: "Paid",
        payment_status_remarks: manualRemarks,
        payment_status_date: manualPaymentDate,
      });

      if (response.status === 200) {
        toast.success(`Payment marked as ${manualPaymentModal.nextStatus}`);

        setManualPaymentModal({
          open: false,
          child: null,
          nextStatus: "Paid",
        });

        setManualRemarks("");
        setRemarksError("");
        resetDateForm({ payment_status_date: "" });

        await onRefresh();
      } else {
        toast.error(
          extractErrorMessage(response, "Failed to update payment")
        );
      }
    } catch (err: any) {
      toast.error(
        extractErrorMessage(
          err?.response?.data,
          "Failed to update payment"
        )
      );
    } finally {
      setManualPayLoading(null);
    }
  };

  const handleStartTrial = async (childIds: number[]) => {
    try {
      const payload = {
        Trail: true,
        Record: 'Trail'
      }
      for (const childId of childIds) {
        await updateChild(childId, payload)
      }

      toast.success("Free Trial Activated");
      closeBuyNow();
    } catch (error) {
      toast.error("Unable to activate free trial");
    }
  };

  function extractErrorMessage(response: any, fallback: string): string {
    const errors = response?.errors;
    if (typeof errors === 'string') return errors;
    if (errors?.error) return errors.error;
    if (errors?.non_field_errors) {
      return Array.isArray(errors.non_field_errors)
        ? errors.non_field_errors[0]
        : String(errors.non_field_errors);
    }
    if (errors && typeof errors === 'object') {
      const firstKey = Object.keys(errors)[0];
      if (firstKey) {
        const val = errors[firstKey];
        return Array.isArray(val) ? val[0] : String(val);
      }
    }
    if (response?.message) return response.message;
    return fallback;
  }

  // ─────────────────────────────────────────────────────────────
  // Record-based visibility helper for the "Mark as Paid" checkbox.
  // Mirrors the same Record-based branching used for the Pay /
  // Renewal / Paid button so both controls stay in sync:
  //   - Record === "Renewal"                      -> show (unless already Paid)
  //   - Record !== "Renewal" && !Verification      -> show (unless already Paid)
  //   - Record !== "Renewal" && Verified && Active -> show (unless already Paid)
  //   - Record !== "Renewal" && Verified && !Active-> hide (Renewal button shown instead)
  //   - payment_status === "Paid"                  -> always hide
  // ─────────────────────────────────────────────────────────────
  const canShowManualPaidCheckbox = (child: any) => {
    if (!canUpdateChild) return false;
    if (child.payment_status === "Paid") return false;

    if (child.Record === "Renewal") {
      return true;
    }

    if (!child.Verification_Status) {
      return true;
    }

    return child.Status === "Active";
  };

  const handleChildToggleActive = useCallback(
    async (child: any) => {
      if (!child.id || togglingId === child.id) return;

      const newStatus = !child.Is_Active;

      // Optimistic update
      setViewingChildren((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          children: prev.children.map((c: any) =>
            c.id === child.id ? { ...c, Is_Active: newStatus } : c
          ),
        };
      });

      setTogglingId(child.id);

      try {
        const response = await updateChild(child.id, {
          Is_Active: newStatus,
        });

        if (response.status === 200) {
          toast.success(
            `Child ${newStatus ? "activated" : "deactivated"} successfully`
          );
          await onRefresh();
        } else {
          // Rollback
          setViewingChildren((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              children: prev.children.map((c: any) =>
                c.id === child.id ? { ...c, Is_Active: child.Is_Active } : c
              ),
            };
          });

          toast.error(extractErrorMessage(response, "Failed to update child status"));
        }
      } catch (err: any) {
        // Rollback
        setViewingChildren((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            children: prev.children.map((c: any) =>
              c.id === child.id ? { ...c, Is_Active: child.Is_Active } : c
            ),
          };
        });

        toast.error(
          extractErrorMessage(err?.response?.data, "Error updating child status")
        );
      } finally {
        setTogglingId(null);
      }
    },
    [onRefresh, togglingId]
  );


  const handleToggleActive = async (employee: any) => {
    if (!employee.id || togglingId === employee.id) return;

    const newStatus = !employee.Is_Active;

    // Optimistic update — flip instantly in UI
    setLocalUsers((prev) =>
      prev.map((e) => e.id === employee.id ? { ...e, Is_Active: newStatus } : e)
    );
    setTogglingId(employee.id);

    try {
      const response = await updateRegisteredUser(Number(employee.id), { Is_Active: newStatus });
      if (response.status === 200) {
        toast.success(`User ${newStatus ? "activated" : "deactivated"} successfully`);
        await onRefresh();
      } else {
        setLocalUsers((prev) =>
          prev.map((e) => e.id === employee.id ? { ...e, Is_Active: employee.Is_Active } : e)
        );
        toast.error("Failed to update user status");
      }
    } catch {
      setLocalUsers((prev) =>
        prev.map((e) => e.id === employee.id ? { ...e, Is_Active: employee.Is_Active } : e)
      );
      toast.error("Error updating user status");
    } finally {
      setTogglingId(null);
    }
  };

  const columns = [
    {
      key: "name",
      label: "Name",
      render: (_: any, row: any) => {
        const fullName = `${row.First_Name || ""} ${row.Last_Name || ""}`.trim() || row.Username;
        return (
          <div>
            <p className="font-bold text-gray-900 text-base">{fullName}</p>
            <p className="text-sm text-gray-500">@{row.Username}</p>
          </div>
        );
      },
    },
    {
      key: "Email",
      label: "Email",
    },
    {
      key: "Phone",
      label: "Phone",
      render: (value: any) => value || "N/A",
    },
    {
      key: "Address",
      label: "Address",
      render: (value: any) => (
        <span className="max-w-xs truncate block">{value || "N/A"}</span>
      ),
    },
    {
      key: "children",
      label: "Children",
      render: (_: any, row: any) => {
        const kids: any[] = row.children ?? [];
        return (
          <button
            onClick={() => {
              setViewingChildren({ user: row, children: kids });
            }}
            // disabled={!row.Is_Active}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors
    bg-brand-50 hover:bg-brand-100 text-brand-700 cursor-pointer`
            }
          >
            {kids.length > 0
              ? `${kids.length} Child${kids.length > 1 ? "ren" : ""}`
              : "0 Children"}
          </button>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      render: (_: any, row: any) => (
        <button
          onClick={() => handleToggleActive(row)}
          disabled={togglingId === row.id}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer disabled:opacity-50 ${row.Is_Active ? "bg-green-500" : "bg-red-500"
            }`}
          title={row.Is_Active ? "Active - Click to deactivate" : "Inactive - Click to activate"}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${row.Is_Active ? "translate-x-6" : "translate-x-1"
              }`}
          />
        </button>
      ),
    },
  ];


  return (
    <>
      <div className="space-y-6">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          itemsPerPage={20}
          onPageChange={onPageChange}
        />
        <GenericTable
          buttonName={canCreate ? "Add User" : undefined}
          handleAdd={canCreate ? handleAdd : undefined}
          columns={columns}
          data={localUsers}
          loading={loading}
          onView={setViewingUser}
          onEdit={canUpdate ? handleEdit : undefined}
          title='Registered Users'
          emptyMessage="No users found"
        />
      </div>

      {/* User Modal */}
      {isModalOpen && (
        <UserModal
          user={editingUser}
          onClose={() => setIsModalOpen(false)}
          onSuccess={async (createdUsername) => {
            setIsModalOpen(false);
            await onRefresh();
            if (createdUsername) setJustCreatedUser(createdUsername);
          }}
        />
      )}

      {/* User Created Success Modal */}
      {justCreatedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-br from-green-400 to-green-600 px-6 py-8 flex flex-col items-center text-center relative">
              <button
                onClick={() => setJustCreatedUser(null)}
                className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>

              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mb-4 shadow-lg">
                <UserCheck className="w-8 h-8 text-green-500" />
              </div>

              <h3 className="text-xl font-bold text-white">
                User Created!
              </h3>

              <p className="text-green-100 text-sm mt-1">
                {justCreatedUser} has been registered successfully.
              </p>
            </div>
            {canCreateChild && (<div className="p-6 space-y-3">
              <p className="text-sm text-gray-500 text-center">Would you like to add a child for this user?</p>
              <button
                onClick={() => {
                  setChildParentUsername(justCreatedUser);
                  setEditingChild(null);
                  setChildModalOpen(true);
                  setJustCreatedUser(null);
                }}
                className="w-full flex items-center justify-center gap-2 bg-brand-500 hover:bg-brand-700 text-white font-semibold py-2.5 rounded-xl transition-colors"
              >
                <Plus className="w-4 h-4" /> Add Child
              </button>
              <button
                onClick={() => setJustCreatedUser(null)}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 rounded-xl transition-colors"
              >
                Skip for now
              </button>
            </div>)}
          </div>
        </div>
      )}

      {/* View User Modal */}
      {viewingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">User Details</h3>
              <button
                onClick={() => setViewingUser(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-500">Username</p>
                <p className="text-gray-900 text-base">{viewingUser.Username}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Email</p>
                <p className="text-gray-900 text-base">{viewingUser.Email}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">First Name</p>
                <p className="text-gray-900 text-base">{viewingUser.First_Name || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Last Name</p>
                <p className="text-gray-900 text-base">{viewingUser.Last_Name || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Phone</p>
                <p className="text-gray-900 text-base">{viewingUser.Phone || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Alternative Phone</p>
                <p className="text-gray-900 text-base">{viewingUser.Alternative_Phone || "N/A"}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm font-semibold text-gray-500">Address</p>
                <p className="text-gray-900 text-base">{viewingUser.Address || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Status</p>
                <p className="text-gray-900 text-base">{viewingUser.Is_Active ? "Active" : "Pending"}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Created At</p>
                <p className="text-gray-900 text-base">{new Date(viewingUser.Created_At).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Children Modal */}
      {viewingChildren && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl shadow-2xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center">
                  <Baby className="w-5 h-5 text-brand-500" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {viewingChildren.user.First_Name || viewingChildren.user.Username}'s Children
                  </h3>
                  <p className="text-sm text-gray-500">{viewingChildren.children.length} child{viewingChildren.children.length !== 1 ? 'ren' : ''} registered</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {canCreateChild && viewingChildren.user.Is_Active && (
                  <button
                    onClick={() => { setEditingChild(null); setChildParentUsername(viewingChildren.user.Username); setChildModalOpen(true); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500 hover:bg-brand-700 text-white text-sm font-semibold rounded-lg transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add Child
                  </button>
                )}
                <button onClick={() => setViewingChildren(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="overflow-y-auto p-6 space-y-4">
              {viewingChildren.children.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Baby className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">No children registered</p>
                </div>
              ) : (
                viewingChildren.children.map((child: any, idx: number) => {
                  const isPaid = child.payment_status === "Paid";
                  return (
                    <div key={child.id ?? idx} className="border border-gray-100 rounded-xl overflow-hidden">
                      {/* Child header */}
                      <div className="bg-brand-50 px-5 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="w-7 h-7 bg-brand-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                            {idx + 1}
                          </span>
                          <span className="font-bold text-gray-900 text-base">{child.Child_Name}</span>
                        </div>

                        {viewingChildren.user.Is_Active && (
                          <div className="flex items-center gap-2">
                            {child.Record === "Renewal" && child.Status !== 'Active' && (
                              <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-600 font-semibold">
                                Renewal Pending
                              </span>
                            )}
                            {child.Record !== "Renewal" && child.Verification_Status && (
                              child.Status === 'Active' ? (
                                <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-600 font-semibold">
                                  Active
                                </span>
                              ) : (
                                <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-600 font-semibold">
                                  Expired
                                </span>
                              )
                            )}
                            {child.Child_Status && (
                              <span className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-100 text-blue-700">
                                {child.Child_Status}
                                {child.Child_Status_Count != null ? ` (${child.Child_Status_Count})` : ''}
                              </span>
                            )}
                            <span className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${child.Verification_Status
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                              }`}>
                              {child.Verification_Status
                                ? <><CheckCircle className="w-3.5 h-3.5" />Verified</>
                                : <><XCircle className="w-3.5 h-3.5" /> Pending</>
                              }
                            </span>
                            {canUpdateChild && (
                              <>
                                {child.Record === "Renewal" ? (
                                  child.payment_status !== "Paid" ? (
                                    <button
                                      onClick={() => handlePayChild(child)}
                                      className="flex items-center gap-1 px-3 py-1.5 mx-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                                    >
                                      Pay
                                    </button>
                                  ) : (
                                    <span className="flex items-center gap-1 px-3 py-1.5 mx-2 bg-green-500 text-white text-sm rounded-lg">
                                      Paid
                                    </span>
                                  )
                                ) : child.Verification_Status ? (child.Status === 'Active' ? child.payment_status !== "Paid" ? (
                                  <button
                                    onClick={() => handlePayChild(child)}
                                    className="flex items-center gap-1 px-3 py-1.5 mx-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                                  >
                                    Pay
                                  </button>
                                ) : (
                                  <span
                                    className="flex items-center gap-1 px-3 py-1.5 mx-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                                  >
                                    Paid
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => {
                                      setRenewalChild(true);
                                      setEditingChild(child);
                                      setChildParentUsername(viewingChildren.user.Username);
                                      setChildModalOpen(true);
                                    }}
                                    className="flex items-center gap-1 px-3 py-1.5 mx-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                                  >
                                    Renewal
                                  </button>
                                )) : child.payment_status !== "Paid" ?
                                  (<button
                                    onClick={() => handlePayChild(child)}
                                    className="flex items-center gap-1 px-3 py-1.5 mx-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                                  >
                                    Pay
                                  </button>
                                  ) : (
                                    <span
                                      className="flex items-center gap-1 px-3 py-1.5 mx-2 bg-green-500 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                                    >
                                      Paid
                                    </span>)}</>)}


                            {canShowManualPaidCheckbox(child) && (
                              <label className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={false}
                                  disabled={manualPayLoading === child.id}
                                  onChange={() => {
                                    setManualRemarks("");
                                    setRemarksError("");
                                    resetDateForm({ payment_status_date: "" });

                                    setManualPaymentModal({
                                      open: true,
                                      child,
                                      nextStatus: "Paid",
                                    });
                                  }}
                                  className="w-4 h-4 accent-green-600"
                                />

                                <span className="text-sm font-medium text-gray-700">
                                  {manualPayLoading === child.id
                                    ? "Updating..."
                                    : "Mark as Paid"}
                                </span>
                              </label>
                            )}

                            {
                              canUpdateChild &&
                              (<button
                                onClick={() => { setRenewalChild(false); setEditingChild(child); setChildParentUsername(viewingChildren!.user.Username); setChildModalOpen(true); }}
                                className="p-1.5 text-brand-500 hover:bg-brand-100 rounded-lg transition-colors"
                                title="Edit child"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                              )}
                            <button
                              onClick={() => handleChildToggleActive(child)}
                              disabled={!canUpdateChild}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${togglingId === child.id ? "cursor-not-allowed" : "cursor-pointer"
                                }`}
                              style={{
                                backgroundColor: child.Is_Active ? "#22c55e" : "#ef4444",
                              }}
                              title={child.Is_Active ? "Deactivate" : "Activate"}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${child.Is_Active ? "translate-x-6" : "translate-x-1"
                                  }`}
                              />
                            </button>
                          </div>)}
                      </div>
                      <div className="flex justify-end w-100vw mt-1 mr-1">
                        <button
                          onClick={() => getChildHistory(child.Line_item_Id)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-lg transition-colors"
                        >
                          History
                        </button></div>
                      <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Age</p>
                          <p className="text-gray-900 font-medium mt-0.5">{child.Age ?? 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Class</p>
                          <p className="text-gray-900 font-medium mt-0.5">{child.Class ?? 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Section</p>
                          <p className="text-gray-900 font-medium mt-0.5">{child.Section ?? 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Roll No</p>
                          <p className="text-gray-900 font-medium mt-0.5">{child.Roll_No ?? 'N/A'}</p>
                        </div>
                        {/* <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">State</p>
                        <p className="text-gray-900 font-medium mt-0.5">{child.State ?? 'N/A'}</p>
                      </div> */}
                        <div className="sm:col-span-1">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">School</p>
                          <p className="text-gray-900 font-medium mt-0.5">{child.School ?? 'N/A'}</p>
                          {child.School_Code && (
                            <p className="text-xs text-gray-400">{child.School_Code}</p>
                          )}
                        </div>
                        <div className="sm:col-span-1">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Duration</p>
                          <p className="text-gray-900 font-normal mt-0.5">
                            {child.Subscription_Start_Date ? child.Subscription_Start_Date : "N/A"}
                            {child.Subscription_End_Date ? ` To ${child.Subscription_End_Date}` : " To N/A"}
                          </p>
                        </div>
                        <div className="col-span-2 sm:col-span-3">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                            Address
                          </p>
                          <p className="text-gray-900 font-medium mt-0.5 break-words whitespace-normal leading-relaxed">
                            {child.Address ?? "N/A"}
                          </p>
                        </div>
                        <div className="flex flex-row gap-5 col-span-2 sm:col-span-3">
                          {child.payment_status_remarks && <div className="col-span-2 sm:col-span-3">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                              Payment Remarks
                            </p>
                            <p className="text-gray-900 font-medium mt-0.5 break-words whitespace-normal leading-relaxed">
                              {child.payment_status_remarks ?? "N/A"}
                            </p>
                          </div>}
                          {child.payment_status_date && <div className="col-span-2 sm:col-span-3">
                            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                              Payment Date
                            </p>
                            <p className="text-gray-900 font-medium mt-0.5">
                              {child.payment_status_date ?? "N/A"}
                            </p>
                          </div>}
                        </div>
                        {/* Range / Pricing */}
                        {child.Range && (
                          <div className="col-span-2 sm:col-span-3 mt-1">
                            <div className="flex items-center gap-2 mb-2">
                              <BookOpen className="w-4 h-4 text-brand-500" />
                              <span className="text-xs font-bold text-brand-700 uppercase tracking-wide">Subscription Range</span>
                            </div>
                            <div className="bg-brand-50 rounded-xl px-4 py-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
                              <div>
                                <p className="text-xs text-gray-500">Period</p>
                                <p className="font-semibold text-gray-900">{child.Range.Period ?? 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">KM Range</p>
                                <p className="font-semibold text-gray-900">{child.Lal_Lon_Km ?? 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Base Price</p>
                                <p className="font-semibold text-gray-900">₹{child.Range.Base_Price?.toLocaleString() ?? 'N/A'}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Final Amount</p>
                                <p className="font-bold text-brand-700 text-base">₹{child.Range.Final_Amount?.toLocaleString() ?? 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
      )}
      {historyModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl shadow-2xl max-h-[90vh] flex flex-col">

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Child History
                </h3>

                <p className="text-sm text-gray-500">
                  Subscription and payment history
                </p>
              </div>

              <button
                onClick={() => {
                  setHistoryModalOpen(false);
                  setChildHistory([]);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto p-6 space-y-4">

              {historyLoading ? (
                <div className="text-center py-12 text-gray-500">
                  Loading history...
                </div>
              ) : childHistory.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />

                  <p className="font-medium">
                    No history found
                  </p>
                </div>
              ) : (
                childHistory.map((history: any, index: number) => (
                  <div
                    key={`${history.Child_Id}-${index}`}
                    className="border border-gray-100 rounded-xl overflow-hidden"
                  >

                    {/* Child Header */}
                    <div className="bg-brand-50 px-5 py-3 flex items-center justify-between">
                      <div>
                        <p className="font-bold text-gray-900">
                          {history.Child_Name || "N/A"}
                        </p>

                        <p className="text-xs text-gray-500 mt-0.5">
                          {history.Child_Id || "N/A"}
                        </p>
                      </div>

                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold ${history.Subscription_Status === "Active"
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                          }`}
                      >
                        {history.Subscription_Status || "N/A"}
                      </span>
                    </div>

                    {/* Details */}
                    <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-4">

                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                          Subscription Code
                        </p>

                        <p className="text-gray-900 font-medium mt-0.5">
                          {history.Subscription_Code || "N/A"}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                          Record
                        </p>

                        <p className="text-gray-900 font-medium mt-0.5">
                          {history.Record || "N/A"}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                          Payment Status
                        </p>

                        <p className="text-gray-900 font-medium mt-0.5">
                          {history.payment_status || "N/A"}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                          Start Date
                        </p>

                        <p className="text-gray-900 font-medium mt-0.5">
                          {history.Subscription_Start_Date || "N/A"}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                          End Date
                        </p>

                        <p className="text-gray-900 font-medium mt-0.5">
                          {history.Subscription_End_Date || "N/A"}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                          Trial
                        </p>

                        <p className="text-gray-900 font-medium mt-0.5">
                          {history.Trail ? "Yes" : "No"}
                        </p>
                      </div>

                      {/* Range / Pricing */}
                      {history.Range && (
                        <div className="col-span-2 sm:col-span-3 mt-2">

                          <div className="flex items-center gap-2 mb-2">
                            <BookOpen className="w-4 h-4 text-brand-500" />

                            <span className="text-xs font-bold text-brand-700 uppercase tracking-wide">
                              Subscription Range
                            </span>
                          </div>

                          <div className="bg-brand-50 rounded-xl px-4 py-3 grid grid-cols-2 sm:grid-cols-4 gap-3">

                            <div>
                              <p className="text-xs text-gray-500">
                                Period
                              </p>

                              <p className="font-semibold text-gray-900">
                                {history.Range.Period || "N/A"}
                              </p>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500">
                                KM Range
                              </p>

                              <p className="font-semibold text-gray-900">
                                {history.Range.KM_Range || "N/A"}
                              </p>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500">
                                Base Price
                              </p>

                              <p className="font-semibold text-gray-900">
                                ₹{Number(history.Range.Base_Price || 0).toLocaleString()}
                              </p>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500">
                                Final Amount
                              </p>

                              <p className="font-bold text-brand-700 text-base">
                                ₹{Number(history.Range.Final_Amount || 0).toLocaleString()}
                              </p>
                            </div>

                          </div>

                          {/* Pricing Details */}
                          <div className="bg-gray-50 rounded-xl px-4 py-3 mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">

                            <div>
                              <p className="text-xs text-gray-500">
                                Base Distance
                              </p>

                              <p className="font-semibold text-gray-900">
                                {history.Range.Base_Distance ?? "N/A"} KM
                              </p>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500">
                                Distance Step
                              </p>

                              <p className="font-semibold text-gray-900">
                                {history.Range.Distance_Step ?? "N/A"}
                              </p>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500">
                                Rate / Meter
                              </p>

                              <p className="font-semibold text-gray-900">
                                {history.Range.Rate_Per_Meter ?? "N/A"}
                              </p>
                            </div>

                            <div>
                              <p className="text-xs text-gray-500">
                                Discount
                              </p>

                              <p className="font-semibold text-gray-900">
                                ₹{Number(history.Range.Discount_Amount || 0).toLocaleString()}
                                {" "}
                                ({history.Range.Discount_Percentage ?? 0}%)
                              </p>
                            </div>

                          </div>
                        </div>
                      )}

                      {/* Payment Details */}
                      <div className="col-span-2 sm:col-span-3 mt-2">

                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                            Payment Details
                          </span>
                        </div>

                        <div className="bg-gray-50 rounded-xl px-4 py-3 grid grid-cols-2 sm:grid-cols-3 gap-3">

                          <div>
                            <p className="text-xs text-gray-500">
                              Payment Status
                            </p>

                            <p className="font-semibold text-gray-900">
                              {history.payment_status || "N/A"}
                            </p>
                          </div>

                          <div>
                            <p className="text-xs text-gray-500">
                              Payment By
                            </p>

                            <p className="font-semibold text-gray-900">
                              {history.payment_status_by || "N/A"}
                            </p>
                          </div>

                          <div>
                            <p className="text-xs text-gray-500">
                              Payment Date
                            </p>

                            <p className="font-semibold text-gray-900">
                              {history.payment_status_date || "N/A"}
                            </p>
                          </div>

                        </div>
                      </div>

                    </div>
                  </div>
                ))
              )}

            </div>
          </div>
        </div>
      )}

      <PaymentModal
        open={openBuyNowModal}
        onClose={closeBuyNow}
        data={paymentData}
        onBuyNow={handleBuyNow}
        rewardBalance={rewardBalance}
        offers={offers}
        specialOffers={specialOffers}
        selectedOfferAmount={selectedOfferAmount}
        setSelectedOfferAmount={setSelectedOfferAmount}
        usedRewardAmount={usedRewardAmount}
        setUsedRewardAmount={setUsedRewardAmount}
        paymentCalculation={paymentCalculation}
        showSavingsModal={showSavingsModal}
        onStartTrial={handleStartTrial}
        setSelectedSpecialOfferAmount={setSelectedSpecialOfferAmount}
        selectedSpecialOfferAmount={selectedSpecialOfferAmount}
      />
      <PaymentProcessingLoader visible={isProcessingPayment} />

      {/* Add / Edit Child Modal */}
      <AddChildModal
        isOpen={childModalOpen}
        onClose={async () => { setRenewalChild(false); setChildModalOpen(false); setEditingChild(null); onRefresh(); }}
        onSuccess={async () => {
          setChildModalOpen(false);
          setEditingChild(null);
          setViewingChildren(null);
          await onRefresh();
        }}
        editingChild={editingChild}
        parentUsername={childParentUsername}
        renewalChild={renewalChild}
      />

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-xl">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Delete User</h3>
            <p className="text-gray-600 mb-6 text-base">
              Are you sure you want to delete user <strong>{showDeleteConfirm.Username}</strong>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white font-semibold py-2.5 rounded-lg transition-all text-base"
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(null)}
                disabled={deleting}
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-900 font-semibold py-2.5 rounded-lg transition-all text-base"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {manualPaymentModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">

            <div className="px-6 py-5 border-b">
              <h2 className="text-xl font-bold">
                Mark as Manually Paid
              </h2>

              <p className="text-sm text-gray-500 mt-1">
                Enter the reason for manually marking this payment as paid
              </p>
            </div>

            <div className="p-6">

              <label className="block text-sm font-semibold mb-2">
                Remarks <span className="text-red-500">*</span>
              </label>

              <textarea
                rows={4}
                value={manualRemarks}
                onChange={(e) => {
                  setManualRemarks(e.target.value);
                  if (remarksError) setRemarksError("");
                }}
                placeholder="Enter remarks..."
                className={`w-full rounded-lg border px-3 py-2 resize-none focus:outline-none ${remarksError
                  ? "border-red-500"
                  : "border-gray-300 focus:border-brand-500"
                  }`}
              />
              {remarksError && (
                <p className="text-red-500 text-sm mt-1">
                  {remarksError}
                </p>
              )}

              <div className="mt-4">
                <DatePicker
                  control={dateControl}
                  name="payment_status_date"
                  label="Payment Date"
                  required
                  error={dateErrors.payment_status_date?.message}
                  maxDate={new Date().toISOString().split("T")[0]}
                  transform={(value: string) => {
                    if (dateErrors.payment_status_date) clearDateErrors("payment_status_date");
                    return value;
                  }}
                />
              </div>

            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t">

              <button
                onClick={() => {
                  setManualPaymentModal({
                    open: false,
                    child: null,
                    nextStatus: "Paid",
                  });
                  setManualRemarks("");
                  setRemarksError("");
                  resetDateForm({ payment_status_date: "" });
                }}
                className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200"
              >
                Cancel
              </button>

              <button
                onClick={handleManualPaid}
                disabled={manualPayLoading !== null}
                className="px-5 py-2 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold"
              >
                {manualPaymentModal.nextStatus === "Paid"
                  ? "Confirm Paid"
                  : "Mark Pending"}
              </button>

            </div>
          </div>
        </div>
      )}
      <ConfirmationModal
        title="Confirm Payment"
        isOpen={showManualPaidConfirmation}
        text="Are you sure you want to mark this payment as paid manually? Once marked as paid, this action cannot be undone."
        onYes={confirmManualPaid}
        onNo={() => {
          setShowManualPaidConfirmation(false);
        }}
      />

    </>
  );
}